/**
 * Inner slant depth as a fraction of each score-panel width.
 * Slightly steeper cut so primaries read closer to the left/right edges.
 * Must stay in sync: ScoreboardSkewScoreBg clip-path, ScoreboardSkewDividerSvg line,
 * and `renderMomentHeader` trapezoid fill in sharedFunctions.
 */
export const SCOREBOARD_TRAP_SKEW_FRAC = 0.21 * 1.2 + 0.006;

/** Gap between trapezoid fill and parallel divider strip, perpendicular to the slant (overlay px). */
export const SCOREBOARD_SHAPE_DIVIDER_GAP_PX = 2;

/** Divider strip thickness (fraction of score-panel height via cqh). */
export const SCOREBOARD_DIVIDER_THICKNESS_CQH_FRAC = 0.036;

/** Bottom inner corner X as % of panel width (leading: BR.x; trailing: BL.x). */
export const SCOREBOARD_TRAP_LEADING_BOTTOM_PCT = Math.round((1 - SCOREBOARD_TRAP_SKEW_FRAC) * 100);
export const SCOREBOARD_TRAP_TRAILING_BOTTOM_PCT = Math.round(SCOREBOARD_TRAP_SKEW_FRAC * 100);

/** Moment title horizontal reserve so score panels do not shift with copy length (HTML `ch` + canvas measure). */
export const SCOREBOARD_MOMENT_TITLE_RESERVE_CHARS = 25;

/** Team logo slots on PNG/GIF raster export vs base layout (`renderMomentHeader` only). */
export const SCOREBOARD_LOGO_DOWNLOAD_MULT = 1.2;

/**
 * Mobile PNG/GIF export: trap score numerals — WebKit renders canvas scores smaller than HTML overlay.
 */
export const SCOREBOARD_MOBILE_DOWNLOAD_SCORE_BOOST = 1.85;

/**
 * Mobile PNG/GIF export: stadium name — same WebKit canvas compensation as trap scores.
 */
export const SCOREBOARD_MOBILE_DOWNLOAD_STADIUM_BOOST = 1.85;

/** Overlay + raster ref: stadium row (Montserrat) in px at 400px banner width. */
export const SCOREBOARD_STADIUM_FONT_PX = 8;

/** Raster fit-loop floor for stadium copy at 400px ref — never shrink below scaled value (was hard-coded 5px). */
export const SCOREBOARD_STADIUM_FONT_MIN_REF_PX = 7;

/**
 * Raster export only (PNG / full GIF): additional scale on team logos + moment title vs values already
 * scaled by vertical boost and logo download multiplier.
 */
export const SCOREBOARD_RASTER_HEADER_COPY_EXTRA_MULT = 1.15;

/** My Moments surfaces: slightly smaller scoreboard logos in the HTML overlay. */
export const SCOREBOARD_LOGO_MY_MOMENTS_UI_MULT = 0.9;

/**
 * Jersey score typography on the HTML overlay: max size (px). Canvas raster uses the same ref via
 * {@link SCOREBOARD_SCORE_FONT_PX} in `renderMomentHeader`. UI clamp ties trap + bar (see `styled.ts`).
 */
export const SCOREBOARD_SCORE_FONT_PX = 46;

/** Minimum score digit height on HTML scoreboard (overlay + My Moments header) when the panel is very narrow. */
export const SCOREBOARD_SCORE_FONT_MIN_PX_UI = 16;

/**
 * Score text scales with the score-trap panel width (`cqi` = 1% of {@link ScoreboardSkewScoreInner} inline size).
 * ~24–26 keeps two digits readable on small phones without exceeding {@link SCOREBOARD_SCORE_FONT_PX}.
 */
export const SCOREBOARD_SCORE_FONT_CQI = 25;

/** Overlay + raster ref: max moment title (Jersey) in px at wide layouts. */
export const SCOREBOARD_MOMENT_TITLE_FONT_PX = 21;

/**
 * HTML scoreboard only: minimum moment title (Jersey) before wrapping; nudged up so multi-line titles
 * stay slightly clearer when space is tight (fluid size still uses {@link SCOREBOARD_MOMENT_TITLE_FONT_CQW}).
 */
export const SCOREBOARD_MOMENT_TITLE_FONT_MIN_PX_UI = 8;

/**
 * Max share of the bar width for the center copy column (`cqw` on overlay). Extra-wide bars narrow the
 * pill further via `@container` rules on {@link ScoreboardCenterPill} so score traps + logos keep room.
 */
export const SCOREBOARD_CENTER_PILL_MAX_CQW = 58;

/**
 * Fluid title: `cqw` on {@link ScoreboardOverlay}. Lower = smaller description when horizontal space is tight.
 */
export const SCOREBOARD_MOMENT_TITLE_FONT_CQW = 3.55;

/** HTML overlay stadium row — mirrors {@link ScoreboardStadium} clamp in styled.ts. */
export const SCOREBOARD_STADIUM_FONT_MIN_PX_UI = 5;
export const SCOREBOARD_STADIUM_FONT_MAX_PX_UI = 9;
export const SCOREBOARD_STADIUM_FONT_CQW = 1.35;

/** HTML overlay trap scores — bar-relative leg of max(cqi, cqw) in styled.ts. */
export const SCOREBOARD_SCORE_FONT_CQW = 5.25;

const clampPx = (value: number, min: number, max: number): number => (
  Math.max(min, Math.min(max, value))
);

/**
 * Typography sizes from the My Moments HTML scoreboard ({@link FanMomentScoreboardBar} / styled.ts).
 * Canvas raster uses these as the base, then scales by the same export multiplier as the moment title.
 */
export function computeScoreboardOverlayTypography(
  bannerWidth: number,
  scorePanelInnerHeight: number,
  viewportWidth?: number,
): { titlePx: number; stadiumPx: number; scorePx: number } {
  const titlePx = clampPx(
    (bannerWidth * SCOREBOARD_MOMENT_TITLE_FONT_CQW) / 100,
    SCOREBOARD_MOMENT_TITLE_FONT_MIN_PX_UI,
    SCOREBOARD_MOMENT_TITLE_FONT_PX,
  );
  const cqwStadium = (bannerWidth * SCOREBOARD_STADIUM_FONT_CQW) / 100;
  const vwStadium = viewportWidth && viewportWidth > 0
    ? (viewportWidth * SCOREBOARD_STADIUM_FONT_CQW) / 100
    : 0;
  const stadiumPx = clampPx(
    Math.max(cqwStadium, vwStadium),
    SCOREBOARD_STADIUM_FONT_MIN_PX_UI,
    SCOREBOARD_STADIUM_FONT_MAX_PX_UI,
  );
  const scoreFluid = Math.max(
    (scorePanelInnerHeight * SCOREBOARD_SCORE_FONT_CQI) / 100,
    (bannerWidth * SCOREBOARD_SCORE_FONT_CQW) / 100,
  );
  const scorePx = clampPx(
    scoreFluid,
    SCOREBOARD_SCORE_FONT_MIN_PX_UI,
    SCOREBOARD_SCORE_FONT_PX,
  );
  return { titlePx, stadiumPx, scorePx };
}
